import type { Rule } from "../types";
import { makeFiring, toIsoDate } from "../util";

const EVIDENCE = "espen_2021_appetite_suppressed";

/**
 * Tirzepatide is a weekly subcutaneous dose. v1 stores it as a Supplement
 * (the BW7 brief deferred Medication entity to BW8); we derive "dose due"
 * from the most recent intake — if it's been ≥ 7 days, the next dose is due.
 */
function isTirzepatide(name: string): boolean {
  return /tirzepatide|mounjaro/i.test(name);
}

export const tirzepatideDoseDue: Rule = {
  id: "tirzepatide_dose_due_today",
  domain: "health.nutrition",
  topics: ["medication", "tirzepatide", "weekly_dose"],
  evidence_refs: [EVIDENCE],
  title: () => "Tirzepatide dose due",
  evaluate(ctx) {
    const tirz = ctx.supplements.find(
      (s) => isTirzepatide(s.name) && s.status === "active",
    );
    if (!tirz) return null;
    const intakes = ctx.supplement_intakes
      .filter((i) => i.supplement_id === tirz.id)
      .sort((a, b) => (a.taken_at < b.taken_at ? 1 : -1));
    const last = intakes[0];

    const today = toIsoDate(ctx.now);
    if (last && last.taken_at.startsWith(today)) return null;

    const dueDate = last
      ? new Date(new Date(last.taken_at).getTime() + 7 * 86_400_000)
      : ctx.now;
    if (dueDate.getTime() > ctx.now.getTime() + 12 * 3_600_000) return null;

    const daysOverdue = Math.max(
      0,
      Math.floor((ctx.now.getTime() - dueDate.getTime()) / 86_400_000),
    );
    const severity = daysOverdue >= 1 ? "warn" : "neutral";
    const msg = !last
      ? `No tirzepatide dose logged yet. Schedule the first injection.`
      : daysOverdue === 0
        ? `Tirzepatide dose due today.`
        : `Tirzepatide dose ${daysOverdue} day${daysOverdue === 1 ? "" : "s"} overdue.`;

    return makeFiring({
      rule_id: "tirzepatide_dose_due_today",
      now: ctx.now,
      tz: ctx.tz,
      severity,
      message: msg,
      evidence_refs: [EVIDENCE],
      factors: [
        {
          name: "days_overdue",
          value: daysOverdue,
          contribution: "log injection",
          evidence_ref: EVIDENCE,
        },
      ],
      target_surface: "today",
      target_action: { kind: "open_supplement_log" },
    });
  },
};
